import { useState, useEffect } from "react";
import { Button } from "../components/ui/button";
import { Zap, Building2, Activity, LogOut, Menu, X, Bell, BarChart3, TestTube, Database } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import OrganizationsManagement from "../components/OrganizationsManagement";
import SystemHealthTabSimple from "../components/SystemHealthTabSimple";
import DashboardStats from "../components/DashboardStats";
import ActionsManagement from "../components/ActionsManagement";
import DatabaseAccessPage from "../components/DatabaseAccessPage";
import PayoutsManagement from "../components/PayoutsManagement";
import { BACKEND_URL } from "../config/api";

// BACKEND_URL imported from config/api

const SuperAdminDashboardV2 = () => {
  const navigate = useNavigate();
  const [activeView, setActiveView] = useState("dashboard");
  // Initialize sidebar state based on screen size
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    // On initial load, expand sidebar only on desktop (>= 1024px)
    return typeof window !== 'undefined' ? window.innerWidth < 1024 : true;
  });
  const [pendingCount, setPendingCount] = useState(0);

  // Handle window resize to ensure sidebar is accessible on all screen sizes
  useEffect(() => {
    const handleResize = () => {
      const isDesktop = window.innerWidth >= 1024;
      // Auto-expand on desktop, auto-collapse on mobile
      setSidebarCollapsed(!isDesktop);
    };

    // Listen for window resize events
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []); // Empty dependency array to avoid re-running

  // Fetch pending organizations count for notification bell
  useEffect(() => {
    const fetchPendingCount = async () => {
      try {
        const token = localStorage.getItem("jwtToken");
        const response = await axios.get(`${BACKEND_URL}/api/platform/organizations`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const pending = response.data.filter(org => org.status === 'pending').length;
        setPendingCount(pending);
      } catch (error) {
        console.error("Error fetching pending count:", error);
      }
    };

    fetchPendingCount();
    // Poll every 30 seconds for new signups
    const interval = setInterval(fetchPendingCount, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleLogout = () => {
    localStorage.removeItem("jwtToken");
    localStorage.removeItem("userType");
    localStorage.removeItem("userData");
    toast.success("Logged out successfully");
    navigate("/login");
  };

  const menuItems = [
    { id: "dashboard", label: "Dashboard", icon: BarChart3 },
    { id: "accounts", label: "Accounts", icon: Building2, badge: pendingCount },
    { id: "database", label: "Database", icon: Database },
    { id: "health", label: "System Health", icon: Activity },
  ];

  return (
    <div className="flex h-screen bg-white">
      {/* Mobile Backdrop */}
      {!sidebarCollapsed && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setSidebarCollapsed(true)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`${
          !sidebarCollapsed ? 'w-64' : 'w-0'
        } fixed lg:relative transition-all duration-300 bg-white flex flex-col h-full z-50 overflow-hidden`}
      >
        {/* Sidebar Header */}
        <div className="p-6">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-blue-600">
              <Zap className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">
                Booking<span className="text-blue-600">Flows</span>
              </h1>
              <p className="text-xs text-gray-500">
                Super Admin Portal
              </p>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-3 space-y-1 overflow-y-auto">
          {menuItems.map((item) => {
            const Icon = item.icon;
            const isActive = activeView === item.id;
            
            return (
              <button
                key={item.id}
                onClick={() => {
                  setActiveView(item.id);
                  if (window.innerWidth < 1024) setSidebarCollapsed(true);
                }}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                  isActive 
                    ? 'bg-blue-600 text-white' 
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                <Icon className="h-5 w-5" />
                <span className="font-medium">{item.label}</span>
                {item.badge > 0 && (
                  <span className={`ml-auto px-2 py-0.5 rounded-full text-xs font-bold ${
                    isActive ? 'bg-white text-blue-600' : 'bg-blue-600 text-white'
                  }`}>
                    {item.badge}
                  </span>
                )}
              </button>
            );
          })}
        </nav>

        {/* Sidebar Footer */}
        <div className="p-4">
          <Button
            variant="ghost"
            className="w-full justify-start text-red-600 hover:bg-red-50"
            onClick={handleLogout}
          >
            <LogOut className="h-5 w-5 mr-3" />
            Logout
          </Button>
        </div>
      </aside>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top Header */}
        <header className="bg-white p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
              >
                {sidebarCollapsed ? <Menu className="h-5 w-5" /> : <X className="h-5 w-5" />}
              </Button>
              
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  {menuItems.find(m => m.id === activeView)?.label || 'Dashboard'}
                </h2>
                <p className="text-sm text-gray-500">Manage your platform</p>
              </div>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="flex-1 overflow-auto p-8 bg-white">
          {activeView === "dashboard" && <DashboardStats />}
          {activeView === "accounts" && <OrganizationsManagement />}
          {activeView === "database" && <DatabaseAccessPage />}
          {activeView === "health" && <SystemHealthTabSimple />}
        </main>
      </div>
    </div>
  );
};

export default SuperAdminDashboardV2;
